import {
  Box,
  Card,
  Divider,
  Grid,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomInput } from "global/components";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import strings from "global/constants/StringConstants";
import { debounceEventHandler, handleErrorMessage } from "helpers/methods";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { HandleTemplateType } from "models/interfaces";
import { useEffect, useRef, useState } from "react";
import { templateOptions } from "screens/Templates/TemplateConstants";
import templateStyles from "screens/Templates/Templates.styles";
import SearchIcon from "assets/icons1/search_icon.svg";
import {
  getTemplateCount,
  getCampaignTemplates,
} from "screens/Templates/TemplatesService";
import PulicTemp_SVG from "assets/images/public_template_svg.svg";
import system_svg from "assets/images/System_Template.svg";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";

interface CustomProps {
  handleDialogClose: Function;
  isDialogOpen: boolean;
  onChooseTemplate: any;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: "auto",
    },
  },
};

const ChooseTemplate = (props: CustomProps) => {
  const theme = useTheme();
  const classes = templateStyles(theme);
  const [selectedDropdownValue, setSelectedDropdownValue] =
    useState<string>("all");
  const [loading, setLoading] = useState<boolean>(false);
  const [templatesFilters, setTemplatesFilters] = useState<{
    initialLoadCompleted: boolean;
    isLastElementInView: boolean;
    displayTemplates: HandleTemplateType[];
  }>({
    initialLoadCompleted: false,
    isLastElementInView: false,
    displayTemplates: [],
  });
  const [searchState, setSearchState] = useState<{
    page: number;
    searchText: string;
  }>({
    page: 1,
    searchText: "",
  });
  const [count, setCount] = useState<number>(0);
  const [perPageTemplates] = useState<number>(10);
  const observerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchCampaignTemplates();
  }, [searchState, selectedDropdownValue]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !loading &&
          templatesFilters.displayTemplates.length < count
        ) {
          const maxPages = Math.ceil(count / perPageTemplates);
          if (searchState.page < maxPages) {
            setSearchState((prev) => ({ ...prev, page: prev.page + 1 }));
          }
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [
    loading,
    templatesFilters.displayTemplates.length,
    count,
    searchState.page,
  ]);

  const fetchCampaignTemplates = async () => {
    try {
      setLoading(true);
      const countResponse = await getTemplateCount(
        selectedDropdownValue,
        searchState.searchText
      );

      const templatesResponse = await getCampaignTemplates(
        selectedDropdownValue,
        perPageTemplates,
        searchState.page,
        searchState.searchText
      );

      setCount(countResponse);
      setTemplatesFilters((prevFilters) => ({
        ...prevFilters,
        initialLoadCompleted: true,
        displayTemplates: [
          ...prevFilters.displayTemplates,
          ...templatesResponse,
        ],
      }));
      setLoading(false);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchOnChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSearchText = event.target.value.trim();
    if (searchState.searchText !== newSearchText) {
      setTemplatesFilters({
        ...templatesFilters,
        displayTemplates: [],
        isLastElementInView: false,
        initialLoadCompleted: false,
      });
      setSearchState({
        page: 1,
        searchText: newSearchText,
      });
    }
  };

  const handleDropDownChange = (event: any) => {
    setSelectedDropdownValue(event.target.value);
    setTemplatesFilters((prevState) => ({
      ...prevState,
      initialLoadCompleted: false,
      isLastElementInView: false,
      displayTemplates: [],
    }));
    setSearchState({
      ...searchState,
      page: 1,
    });
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <CustomInput
            id="templates_search_box"
            placeHolder="Search templates"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            onChange={debounceEventHandler(
              handleSearchOnChange,
              strings.SEARCH_TIME_OUT
            )}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
            disabled={loading}
          />
          <Divider />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Select
              variant="outlined"
              id="templates_category_dropdown"
              value={selectedDropdownValue}
              onChange={handleDropDownChange}
              sx={classes.dropDown}
              MenuProps={MenuProps}
              disabled={loading}
            >
              {templateOptions.map((data, index: number) => (
                <MenuItem
                  key={index}
                  value={data.value}
                  sx={{
                    ...classes.dropDownMenuItem,
                    padding: "12px",
                  }}
                >
                  {data.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
      </CenteredCardLayout>
    );
  };

  const TemplatesPage = () => {
    return (
      <Stack>
        <Grid container spacing={3}>
          {templatesFilters.displayTemplates.length > 0 ? (
            templatesFilters.displayTemplates.map(
              (template: any, index: number) => {
                return (
                  <Grid item xs={12} sm={6} md={3} key={template.id}>
                    <Box position="relative">
                      {template.category === "System" && (
                        <Box sx={classes.ribbon}>
                          <img src={system_svg} />
                        </Box>
                      )}
                      {template.category === "Public" && (
                        <Box sx={classes.ribbon}>
                          <img src={PulicTemp_SVG} />
                        </Box>
                      )}
                      <Card
                        sx={classes.templateCardStyle}
                        onClick={() => {
                          props.onChooseTemplate(template.jsonContent);
                        }}
                      >
                        <Box
                          component="img"
                          src={template.image}
                          alt={template.title}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />

                        <Box sx={classes.nameBox}>
                          <Typography
                            variant="subtitle1"
                            sx={classes.templateNameText}
                          >
                            {template.name}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  </Grid>
                );
              }
            )
          ) : (
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
              height={180}
              spacing={3}
              width="100%"
            >
              <Typography variant="h6">
                There are no templates, click here to{" "}
                <span>
                  <Link
                    underline="hover"
                    variant="h6"
                    onClick={() => history.push(viewpaths.templateDesignViewPath)}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    create a new template.
                  </Link>
                </span>
              </Typography>
            </Stack>
          )}
          {loading && searchState.page !== 1 && renderSkeletons()}
          <div ref={observerRef}></div>
        </Grid>
      </Stack>
    );
  };

  const renderSkeletons = () => {
    return Array.from({ length: 8 }).map((_, index) => (
      <Grid item xs={12} sm={6} md={3} key={`skeleton-${index}`}>
        <Skeleton
          variant="rounded"
          animation="wave"
          sx={{ width: "100%", height: 270 }}
        />
        <Skeleton variant="text" width={300} />
        <Skeleton variant="text" width={300} />
      </Grid>
    ));
  };

  const getTemplatesPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        {getFilters()}
        {loading && searchState.page === 1 ? (
          <Stack>
            <Grid container spacing={3}>
              {renderSkeletons()}
            </Grid>
          </Stack>
        ) : (
          TemplatesPage()
        )}
      </Stack>
    );
  };

  const getBody = () => {
    return <Stack spacing={3}>{getTemplatesPage()}</Stack>;
  };

  return (
    <>
      <CustomDialog
        handleDialogClose={props.handleDialogClose}
        isDialogOpen={props.isDialogOpen}
        maxWidth="xl"
        closeButtonVisibility
        closable
        title={
          <Typography variant="h4" color="#7551D0">
            Templates
          </Typography>
        }
        body={getBody()}
      />
    </>
  );
};

export default ChooseTemplate;
